/**
 * §9.3's dependency-map nodes that have no real backend signal at all, regardless of which
 * `PlatformApiClient` is wired up: MinIO cluster status (no MinIO admin API integration),
 * Postgres replication lag (no query against `pg_stat_replication` exists), backup age (no
 * pgBackRest/WAL-G job writes one anywhere), nginx cache stats, and worker/queue depth as a
 * dedicated health signal (the real *data* — `sync_conflicts` — is live via
 * `listSyncConflicts()`, but no route exposes it as a health-node-shaped summary). Extracted
 * to its own module so both `mockPlatformApiClient.ts` (the mock world) and
 * `realPlatformApiClient.ts` (which is otherwise fully real, but has nothing to call for
 * these specific nodes) return the exact same simulated content rather than two fixtures
 * silently drifting apart — see `SystemHealthPage.tsx`, which renders every node here with a
 * `mocked: true` `MockBadge`, live or not.
 */
import type { HealthNode } from "./types";

export function buildMockHealthNodes(syncConflictQueueDepth = 0): HealthNode[] {
  const now = new Date().toISOString();
  return [
    {
      id: "postgres-replication",
      label: "Postgres replication (primary → standby)",
      status: "healthy",
      detail: "replication lag: 340ms",
      mocked: true,
      lastChecked: now,
    },
    {
      id: "backup-job",
      label: "pgBackRest / WAL-G backup job",
      status: "healthy",
      detail: "last successful backup: 3h ago",
      mocked: true,
      lastChecked: now,
    },
    {
      id: "minio-node-1",
      label: "MinIO Node 1",
      status: "healthy",
      detail: "erasure coding: online",
      mocked: true,
      lastChecked: now,
    },
    {
      id: "minio-node-2",
      label: "MinIO Node 2",
      status: "healthy",
      detail: "erasure coding: online",
      mocked: true,
      lastChecked: now,
    },
    {
      id: "minio-node-3",
      label: "MinIO Node 3",
      status: "degraded",
      detail: "disk latency elevated (simulated)",
      mocked: true,
      lastChecked: now,
    },
    {
      id: "minio-node-4",
      label: "MinIO Node 4",
      status: "healthy",
      detail: "erasure coding: online",
      mocked: true,
      lastChecked: now,
    },
    {
      id: "nginx-cache",
      label: "nginx cache layer",
      status: "healthy",
      detail: "cache hit ratio: 92%",
      mocked: true,
      lastChecked: now,
    },
    {
      id: "worker-queue",
      label: "Background workers (sync_conflicts / image processing / PITR)",
      status: syncConflictQueueDepth > 3 ? "degraded" : "healthy",
      detail: `queue depth: ${syncConflictQueueDepth}`,
      mocked: true,
      lastChecked: now,
    },
  ];
}
